import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Shield, Users, TrendingDown, Plane } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

export default function Destinations() {
  const { t } = useTranslation();
  const [citiesRef, citiesVisible] = useScrollAnimation();
  const [whyRef, whyVisible] = useScrollAnimation();

  const cities = t("destinations.cities", { returnObjects: true }) || [];
  const whyItems = t("destinations_page.whyTurkey.items", { returnObjects: true }) || [];
  const whyIcons = [<Shield className="w-6 h-6" />, <Users className="w-6 h-6" />, <TrendingDown className="w-6 h-6" />, <Plane className="w-6 h-6" />];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Medical Tourism Destinations in Turkey | Clinics in Istanbul & Antalya | Monimek</title>
        <meta name="description" content="Discover Monimek's partner hospitals in premier medical tourism destinations like Istanbul and Antalya. World-class healthcare and recovery in beautiful Turkey." />
        <link rel="canonical" href="https://www.monizmek.com/destinations" />
      </Helmet>
      {/* Hero */}
      <section className="page-hero text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/2042188/pexels-photo-2042188.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Istanbul"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 py-12 text-center">
          <span className="overline-text text-[#3FA7D6]">{t("destinations_page.hero.overline")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-outfit tracking-tight mt-3 mb-5" data-testid="destinations-hero-title">
            {t("destinations_page.hero.title")}
          </h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto">{t("destinations_page.hero.subtitle")}</p>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-20 bg-[#F8FAFC]" ref={citiesRef}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`text-center mb-12 anim-init ${citiesVisible ? "in-view" : ""}`}>
            <span className="overline-text">{t("destinations.overline")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight mt-2">
              {t("destinations.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {cities.map((city, i) => (
              <div
                key={i}
                className={`anim-init stagger-${i + 1} ${citiesVisible ? "in-view" : ""} relative rounded-3xl overflow-hidden dest-card cursor-pointer group`}
                style={{ aspectRatio: "16/9" }}
                data-testid={`dest-city-${i}`}
              >
                <img
                  src={city.image}
                  alt={city.name}
                  className="w-full h-full object-cover transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B3C5D]/85 via-[#0B3C5D]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <h3 className="text-3xl font-black text-white font-outfit mb-1">{city.name}</h3>
                  <p className="text-white/75 text-sm mb-3">{city.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="inline-block px-3 py-1 bg-[#3FA7D6]/30 backdrop-blur-sm border border-[#3FA7D6]/30 text-white text-xs font-semibold rounded-full">
                      {city.saving}
                    </span>
                    <Link
                      to="/contact"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold flex items-center gap-1"
                    >
                      Learn more <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Turkey */}
      <section className="py-20 bg-white" ref={whyRef}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`text-center mb-12 anim-init ${whyVisible ? "in-view" : ""}`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] font-outfit tracking-tight" data-testid="why-turkey-title">
              {t("destinations_page.whyTurkey.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyItems.map((item, i) => (
              <div
                key={i}
                className={`anim-init stagger-${i + 1} ${whyVisible ? "in-view" : ""} bg-[#F8FAFC] rounded-3xl p-7 border border-gray-100 treatment-card text-center`}
                data-testid={`why-turkey-item-${i}`}
              >
                <div className="w-12 h-12 rounded-2xl why-icon flex items-center justify-center text-white mx-auto mb-5">
                  {whyIcons[i]}
                </div>
                <h3 className="font-bold text-[#0B3C5D] text-sm mb-2">{item.title}</h3>
                <p className="text-xs text-[#6B7280] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-gradient py-16">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white font-outfit mb-4">{t("ctaBanner.title")}</h2>
          <p className="text-white/75 mb-8">{t("ctaBanner.subtitle")}</p>
          <Link to="/contact" data-testid="destinations-cta" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0B3C5D] font-bold rounded-full hover:scale-105 transition-all duration-200">
            {t("ctaBanner.button")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
